/* eslint-disable react/prop-types */
import { ReactElement, useEffect, useRef } from 'react'
import { VariableSizeList } from 'react-window'

import { ITEM_HEIGHT } from './MultipleComboBoxItem'
import { MultipleComboBoxProps } from './types'

export const GROUP_ITEM_KEY = 'multiple-comboBox-group-by'
export const GROUP_HEADER_HEIGHT = 44

function useResetCache(itemCount: number) {
  const ref = useRef<VariableSizeList>(null)

  useEffect(() => {
    if (ref.current) {
      ref.current.resetAfterIndex(0, true)
    }
  }, [itemCount])
  return ref
}

type MultipleComboBoxVirtualizedListProps = {
  elements: ReactElement[]
} & Pick<MultipleComboBoxProps, 'value'>

export const MultipleComboBoxVirtualizedList = (props: MultipleComboBoxVirtualizedListProps) => {
  const { elements, value } = props
  const itemCount = elements?.length
  const hasDescription = elements.some(
    (el) => (el.props?.children?.props?.option?.description as string)?.length > 0,
  )
  let elementHeight = hasDescription ? ITEM_HEIGHT + 4 : ITEM_HEIGHT

  const getHeight = () => {
    // recommended perf best practice
    if (itemCount > 5) {
      return 5 * (elementHeight + 4) - 4 // Last item does not have 4px margin-bottom
    }
    return itemCount * (elementHeight + 4) - 4 // Last item does not have 4px margin-bottom
  }

  // reset the `VariableSizeList` cache if data gets updated
  const gridRef = useResetCache(itemCount)

  // when value gets updated, ensure we tell <VariableSizeList>
  // to scroll to the selected option
  useEffect(
    () => {
      if (gridRef && value && gridRef.current) {
        const valueIndex = elements.findIndex(
          (el) => el.props?.children?.props?.option?.value === value,
        )

        if (valueIndex) {
          gridRef.current?.scrollToItem(valueIndex, 'smart')
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value],
  )

  return (
    <VariableSizeList
      itemData={elements}
      height={getHeight()}
      width="100%"
      ref={gridRef}
      innerElementType="div"
      itemSize={(index) => {
        return index === itemCount - 1
          ? elementHeight
          : ((elements[index].key as string) || '').includes(GROUP_ITEM_KEY)
            ? GROUP_HEADER_HEIGHT + (index === 0 ? 8 : 12)
            : elementHeight + 4
      }}
      overscanCount={5}
      itemCount={itemCount}
    >
      {({ style, index }) => <div style={style}>{elements[index]}</div>}
    </VariableSizeList>
  )
}
